import {keepPreviousData, queryOptions} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {SavedView, SavedViewsResponse} from './types'

export const SAVED_VIEWS_QUERY_KEY = 'repo-saved-views'
export const SAVED_VIEW_QUERY_KEY = 'repo-saved-view'

export class SavedViewFetchError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'SavedViewFetchError'
    this.status = status
  }
}

export async function parseFetchErrorMessage(response: Response, fallback: string): Promise<string> {
  return (await parseFetchError(response, fallback)).message
}

export interface ParsedFetchError {
  message: string
  code?: string
}

type FetchSavedViewsTarget =
  | {
      owner: string
      repo: string
      listUrl?: never
    }
  | {
      owner?: never
      repo?: never
      listUrl: string
    }

type FetchSavedViewsOptions = FetchSavedViewsTarget & {
  // Defaults to the repository list endpoint built from `owner`/`repo`. Account
  // level surfaces are not under a repository, so they pass a URL instead.
  signal?: AbortSignal
}

export async function fetchSavedViews({owner, repo, listUrl, signal}: FetchSavedViewsOptions) {
  const response = await verifiedFetchJSON(listUrl ?? `/${owner}/${repo}/issues/views.json`, {signal})
  if (!response.ok) {
    throw new SavedViewFetchError(
      response.status,
      await parseFetchErrorMessage(response, `Failed to fetch saved views (${response.status})`),
    )
  }
  return (await response.json()) as SavedViewsResponse
}

export async function parseFetchError(response: Response, fallback: string): Promise<ParsedFetchError> {
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as {error?: {message?: string; code?: string}} | null
      if (payload?.error?.message) {
        return {message: payload.error.message, code: payload.error.code}
      }
    } catch {
      return {message: fallback}
    }
    return {message: fallback}
  }

  try {
    const text = await response.text()
    if (text) return {message: text}
  } catch {
    return {message: fallback}
  }
  return {message: fallback}
}

export function savedViewsQueryOptions({
  owner,
  repo,
  listUrl,
  initialData,
}: {
  owner: string
  repo: string
  // Defaults to the repository list endpoint. Account level surfaces pass their
  // own; they also pass an owner/repo pair of their own so the cache keys, which
  // the shared mutation helpers write through, stay distinct.
  listUrl?: string
  initialData?: SavedViewsResponse
}) {
  const resolvedListUrl = listUrl ?? `/${owner}/${repo}/issues/views.json`
  return queryOptions({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the list URL is derived from owner/repo; keying on it too would break the exact-key cache writes the shared mutation helpers make.
    queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo] as const,
    placeholderData: keepPreviousData,
    initialData,
    staleTime: initialData ? Infinity : 0,
    queryFn: ({signal}) => fetchSavedViews({listUrl: resolvedListUrl, signal}),
  })
}

export function savedViewQueryOptions({owner, repo, id}: {owner: string; repo: string; id: string}) {
  return queryOptions({
    queryKey: [SAVED_VIEW_QUERY_KEY, owner, repo, id] as const,
    queryFn: async ({signal}) => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views/${encodeURIComponent(id)}.json`, {
        signal,
      })
      if (!response.ok) {
        throw new SavedViewFetchError(
          response.status,
          await parseFetchErrorMessage(response, `Failed to fetch view (${response.status})`),
        )
      }
      return (await response.json()) as {savedView: SavedView; viewerCanManageViews: boolean}
    },
  })
}
